import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from './cx';

type Variant = 'primary' | 'secondary' | 'ink' | 'ghost' | 'danger' | 'signal';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: Variant;
  size?: 'md' | 'sm';
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** Render as a link. Internal paths (starting with "/" but not "//") use client routing unless `external`. */
  href?: string;
  external?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    type = 'button',
    href,
    external,
    icon,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const common = { className: cx('fl-btn', className), 'data-variant': variant, 'data-size': size };
  const inner = (
    <>
      {loading ? <span className="fl-spinner" aria-hidden="true" /> : icon}
      {children}
    </>
  );
  if (href !== undefined) {
    const internal =
      !external &&
      href.startsWith('/') &&
      !href.startsWith('//') &&
      !href.startsWith('/auth/') &&
      !href.startsWith('/api/');
    return internal ? (
      <Link to={href} {...common}>
        {inner}
      </Link>
    ) : (
      <a href={href} {...common}>
        {inner}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      {...common}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {inner}
    </button>
  );
});
